/* eslint-disable */
import React, { useState } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails, Link } from '@mui/material';
import { ExpandMore, OpenInNew } from '@mui/icons-material';
import type { SRDetails, Ticket } from '../../types/release';
import { TicketBadge } from '../common/TicketBadge';

interface CustomerValueProps {
  customerValue: SRDetails[];
}

export const CustomerValue: React.FC<CustomerValueProps> = ({ customerValue }) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const handleChange = (srId: string) => {
    setExpanded((prev) => ({
      ...prev,
      [srId]: !prev[srId],
    }));
  };

  if (customerValue.length === 0) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Customer Value Features
        </Typography>
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No customer value features found for this release.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Try adjusting your filters or search query.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Customer Value Features ({customerValue.length})
      </Typography>
      
      {customerValue.map((sr) => (
        <Accordion
          key={sr.sr_id}
          expanded={expanded[sr.sr_id] || false}
          onChange={() => handleChange(sr.sr_id)}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Link
                href={sr.sr_link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ textDecoration: 'none', fontWeight: 'bold', color: 'primary.main' }}
              >
                {sr.sr_id}
                <OpenInNew sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
              </Link>
              <Typography variant="body1" sx={{ flexGrow: 1 }}>
                {sr.sr_title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
                Status: {sr.sr_status}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {sr.engineering_tickets && sr.engineering_tickets.length > 0 ? (
                (() => {
                  // Deduplicate: filter out tickets that are children of other tickets in the list
                  // (they should only appear nested under their parent)
                  const allTicketIds = new Set(sr.engineering_tickets.map(t => t.id));
                  
                  // Collect all child IDs from children arrays
                  const childIds = new Set<string>();
                  sr.engineering_tickets.forEach(ticket => {
                    if (ticket.children) {
                      ticket.children.forEach((child: any) => {
                        if (typeof child === 'object' && child.id) {
                          childIds.add(child.id);
                        }
                      });
                    }
                  });
                  
                  // Filter out tickets that are children of other tickets
                  const topLevelTickets = sr.engineering_tickets.filter(ticket => {
                    // If ticket has a parent_id, check if that parent is also in the list
                    if (ticket.parent_id && allTicketIds.has(ticket.parent_id)) {
                      return false; // This is a child, exclude it from top-level
                    }
                    // Also exclude if it's in the children arrays of other tickets
                    if (childIds.has(ticket.id)) {
                      return false;
                    }
                    return true; // Top-level ticket (no parent or parent not in list)
                  });
                  
                  return topLevelTickets.map((ticket) => (
                  <Box key={ticket.id} sx={{ mb: 2 }}>
                    <TicketBadge ticket={ticket} />
                    {ticket.children && ticket.children.length > 0 && (
                      <Box sx={{ ml: 4, mt: 1 }}>
                        {ticket.children.map((child) => {
                          // Handle simplified child objects
                          const childTicket: Ticket = typeof child === 'object' && 'type' in child
                            ? child as Ticket
                            : {
                                ...child,
                                type: 'Sub-task',
                                labels: [],
                                link: `https://outsight.atlassian.net/browse/${child.id}`,
                                children: [],
                                is_late: child.is_late || false,
                                validation_issues: [],
                              };
                          return <TicketBadge key={child.id} ticket={childTicket} />;
                        })}
                      </Box>
                    )}
                  </Box>
                  ));
                })()
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No engineering tickets linked to this SR.
                </Typography>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
